// State persistence utility for managing filter state across tabs and page refreshes

const STORAGE_KEY = 'dateFilter';
const TAB_ID_KEY = 'currentTabId';

// Generate unique tab ID
const generateTabId = () => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create tab ID
export const getTabId = () => {
  let tabId = localStorage.getItem(TAB_ID_KEY);
  if (!tabId) {
    tabId = generateTabId();
    localStorage.setItem(TAB_ID_KEY, tabId);
  }
  return tabId;
};

// Save filter state to localStorage
export const saveFilterState = (filterState) => {
  try {
    const stateToSave = {
      ...filterState,
      lastUpdated: Date.now(),
      tabId: getTabId()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    return true;
  } catch (error) {
    console.warn('Failed to save filter state:', error);
    return false;
  }
};

// Load filter state from localStorage
export const loadFilterState = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (!serializedState) {
      return null;
    }
    
    const state = JSON.parse(serializedState);
    
    // Check if state is too old (older than 24 hours)
    const isExpired = state.lastUpdated && (Date.now() - state.lastUpdated) > 24 * 60 * 60 * 1000;
    if (isExpired) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return state;
  } catch (error) {
    console.warn('Failed to load filter state:', error);
    return null;
  }
};

// Clear stored state
export const clearFilterState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TAB_ID_KEY);
    return true;
  } catch (error) {
    console.warn('Failed to clear filter state:', error);
    return false;
  }
};

// Check if state is from current tab
export const isStateFromCurrentTab = (state) => {
  return state && state.tabId === getTabId();
};

// Validate filter state structure - more lenient validation
export const validateFilterState = (state) => {
  if (!state || typeof state !== 'object') {
    return false;
  }

  // At minimum, we need dates or marketplace
  const hasDates = state.startDate && state.endDate;
  const hasMarketplace = Array.isArray(state.marketplace) && state.marketplace.length > 0;
  
  // State is valid if it has either dates or marketplace (or both)
  if (!hasDates && !hasMarketplace) {
    return false;
  }

  // If dates exist, validate their format
  if (hasDates) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
    if (!dateRegex.test(state.startDate) || !dateRegex.test(state.endDate)) {
      return false;
    }
  }

  return true;
};

// Merge persisted state with updateChannel from localStorage if available
export const mergeWithUpdateChannel = (persistedState) => {
  try {
    const updateChannel = JSON.parse(localStorage.getItem("updateChannel"));
    if (updateChannel && Array.isArray(updateChannel) && updateChannel.length > 0) {
      // If persisted state doesn't have marketplace or it's invalid, use updateChannel
      if (!persistedState.marketplace || !Array.isArray(persistedState.marketplace) || persistedState.marketplace.length === 0) {
        return {
          ...persistedState,
          marketplace: updateChannel
        };
      }
      // If persisted state has marketplace, ensure it's synced with updateChannel
      // Merge by preserving checked state from persisted, but adding any new channels from updateChannel
      const persistedMarketplaceMap = new Map(
        persistedState.marketplace.map(m => [m.name || m.id, m])
      );
      
      // Start with updateChannel as base, then apply persisted checked states
      const mergedMarketplace = updateChannel.map(channel => {
        const persistedChannel = persistedMarketplaceMap.get(channel.name || channel.id);
        if (persistedChannel) {
          // Preserve checked state from persisted state
          return {
            ...channel,
            checked: persistedChannel.checked !== undefined ? persistedChannel.checked : channel.checked
          };
        }
        return channel;
      });
      
      return {
        ...persistedState,
        marketplace: mergedMarketplace
      };
    }
  } catch (error) {
    console.warn('Failed to merge with updateChannel:', error);
  }
  
  return persistedState;
};
